import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { bestMonth, monthlyEarnings } from '../../../domain/calculators/commissions';
import { formatBDT } from '../../../domain/formatters/currency';
import { formatMonthKey, formatMonthName, formatNumber } from '../../../domain/formatters/date';
import type { CommissionLedgerEntry } from '../../../domain/entities/CommissionLedgerEntry';
import { Button } from '../ui/Button';
import { MonthlyEarningsTable } from './MonthlyEarningsTable';

export interface MonthlyEarningsChartProps {
  entries: CommissionLedgerEntry[];
  /** Tap a bar (or a table month) to filter the ledger to that `YYYY-MM`. */
  onSelectMonth: (key: string) => void;
  /** Number of rows the totals are based on, when the source fetch was capped. */
  cappedAt?: number;
}

/**
 * Trailing-12-month stacked booking vs installment chart in Asia/Dhaka months. Token colours,
 * legend, role="img" + text summary, and a table alternative. Empty data renders a placeholder.
 */
export function MonthlyEarningsChart({ entries, onSelectMonth, cappedAt }: MonthlyEarningsChartProps) {
  const { t, locale } = useTranslation();
  const [asTable, setAsTable] = useState(false);
  const data = useMemo(() => monthlyEarnings(entries, 12), [entries]);
  const best = useMemo(() => bestMonth(data), [data]);

  if (!best) {
    return <p className="muted" data-testid="monthly-chart-empty">{t('commissions.chartEmpty')}</p>;
  }
  const summary = t('commissions.chartSummary', {
    month: formatMonthKey(best.key, locale),
    amount: formatBDT(best.total, locale),
  });

  return (
    <div data-testid="monthly-chart">
      <div className="row-between chart-meta">
        <p className="muted" data-testid="chart-summary">{summary}</p>
        <Button variant="ghost" size="sm" onClick={() => setAsTable((v) => !v)} data-testid="chart-toggle">
          {t(asTable ? 'commissions.showChart' : 'commissions.showTable')}
        </Button>
      </div>
      {asTable ? (
        <MonthlyEarningsTable data={data} onSelectMonth={onSelectMonth} />
      ) : (
        <div role="img" aria-label={t('commissions.chartAria', { best: summary })} className="chart-box">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }} barCategoryGap="22%">
              <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="#e3e8e5" />
              <XAxis dataKey="key" tick={{ fontSize: 11, fill: '#5c6f66' }} interval={0} tickLine={false}
                axisLine={{ stroke: '#e3e8e5' }} tickFormatter={(k: string) => formatMonthName(k, locale)} />
              <YAxis width={52} tick={{ fontSize: 11, fill: '#5c6f66' }} tickLine={false} axisLine={false}
                tickFormatter={(v: number) => formatNumber(v, locale)} />
              <Tooltip
                cursor={{ fill: 'rgba(13, 124, 61, 0.06)' }}
                contentStyle={{ borderRadius: 10, border: '1px solid #e3e8e5', fontSize: 13 }}
                formatter={(value) => formatBDT(Number(value), locale)}
                labelFormatter={(k) => formatMonthKey(String(k), locale)}
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#5c6f66' }} />
              <Bar dataKey="booking" stackId="a" fill="var(--color-primary)" name={t('commissions.booking')}
                cursor="pointer" onClick={(d) => onSelectMonth((d as unknown as { key: string }).key)} />
              <Bar dataKey="emi" stackId="a" fill="var(--color-accent)" name={t('commissions.installment')}
                radius={[4, 4, 0, 0]} cursor="pointer"
                onClick={(d) => onSelectMonth((d as unknown as { key: string }).key)} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="muted chart-hint">{t('commissions.chartHint')}</p>
      {cappedAt !== undefined && (
        <p className="muted chart-note" data-testid="chart-capped-note">
          {t('commissions.chartLatest100', { count: formatNumber(cappedAt, locale) })}
        </p>
      )}
    </div>
  );
}
